// Core imports
import React, { Fragment, useEffect, useState } from 'react';

// Component imports
import { Button, Card, Col, Row, Typography, Divider, message } from 'antd';
import { Heart, Wallet, Discovery, Setting, Category, ShieldDone, Star } from 'react-iconly';
import SEO from '../components/SEO/SEO';

// Asset imports
import { ReactComponent as PayPalIcon } from '../assets/icons/paypal.svg';
import { ReactComponent as BuyMeACoffeeIcon } from '../assets/icons/buymeacoffee.svg';
import { ReactComponent as MetaMaskIcon } from '../assets/icons/metamask.svg';

// Misc imports
import { SUPPORT_EMAIL, PAYPAL_URL, BUYMEACOFFEE_URL, CRYPTO_WALLET } from '../constants';
import api from '../api';

// What it actually costs to keep the archive online each month. Naming a real
// number converts better than an open-ended ask. It turns "support us" into a
// bill a reader can picture themselves covering a share of.
const MONTHLY_COST_EUR = 12;

// Constant declarations
const { Title, Paragraph } = Typography;

const iconCircleStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 64,
  height: 64,
  borderRadius: '50%',
  marginBottom: 16
};

const donationOptions = [
  {
    key: 'paypal',
    title: 'PayPal',
    description: 'Make a one-time or recurring donation easily through PayPal. Secure and widely trusted worldwide.',
    icon: <PayPalIcon width={28} height={28} />,
    color: '#003087',
    bgGradient: 'linear-gradient(135deg, rgba(0, 48, 135, 0.10), rgba(0, 112, 186, 0.10))',
    url: PAYPAL_URL,
    buttonText: 'Donate with PayPal'
  },
  {
    key: 'buymeacoffee',
    title: 'Buy Me a Coffee',
    description: 'A simple and friendly way to support the project. Even a single coffee makes a difference!',
    icon: <BuyMeACoffeeIcon width={28} height={28} />,
    color: '#FFAA00',
    bgGradient: 'linear-gradient(135deg, rgba(255, 170, 0, 0.12), rgba(255, 210, 80, 0.12))',
    url: BUYMEACOFFEE_URL,
    buttonText: 'Buy a Coffee'
  },
  {
    key: 'crypto',
    title: 'Cryptocurrency',
    description: 'Send ETH or BNB directly to our wallet. Works on Ethereum and Binance Smart Chain (BSC) networks.',
    icon: <MetaMaskIcon width={28} height={28} />,
    color: '#627EEA',
    bgGradient: 'linear-gradient(135deg, rgba(98, 126, 234, 0.10), rgba(150, 170, 255, 0.10))',
    wallet: CRYPTO_WALLET,
    buttonText: 'Copy Wallet Address'
  }
];

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://ndearchive.com';

const DonateScreen = () => {
  const [totalCount, setTotalCount] = useState(null);

  useEffect(() => {
    api.fetchStatistics()
      .then(data => {
        if (data?.totalCount) {
          setTotalCount(data.totalCount);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <Fragment>
      <SEO
        title="Donate | Help Keep It Free"
        description={`NDE Archive is free to read, with no paywall and no ads. Hosting costs about \u20ac${MONTHLY_COST_EUR} a month. Help keep thousands of first-hand near-death experience accounts online.`}
        path="/donate"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Donate | Support NDE Archive',
            description: 'Support NDE Archive, a free passion project. Help cover hosting and keep the platform free.',
            url: `${SITE_URL}/donate`
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: 'Donate', item: `${SITE_URL}/donate` }
            ]
          }
        ]}
      />
      {/* Hero Section */}
      <div className="donate-hero text-center" style={{ padding: '64px 0 48px' }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(145, 149, 252, 0.12), rgba(180, 182, 240, 0.12))',
          marginBottom: 24
        }}>
          <Heart set="bulk" primaryColor="#9195fc" size={40} />
        </div>

        <Title level={1} style={{ fontWeight: 800, marginBottom: 16, letterSpacing: '-0.5px' }}>
          Support NDE Archive
        </Title>

        <Paragraph style={{
          fontSize: 16,
          lineHeight: '28px',
          maxWidth: 1200,
          margin: '0 auto 16px'
        }}>
          {totalCount ? <><strong>{totalCount.toLocaleString()} first-hand accounts</strong> of
          what people experienced as they came close to death</> : <>Thousands of first-hand
          accounts of what people experienced as they came close to death</>}, free to read,
          with no paywall, no subscription, and no advertising. Many were given by people who
          had never told anyone before.
        </Paragraph>

        <Paragraph style={{
          fontSize: 16,
          lineHeight: '28px',
          maxWidth: 1200,
          margin: '0 auto 16px'
        }}>
          Keeping them online costs about <strong>€{MONTHLY_COST_EUR} a month</strong>. That is
          the whole budget: the database lives on a personal home server and the website runs on
          paid hosting. There is no team, no company, and no investor, just one person, which is
          also why the site can't absorb large traffic spikes and occasionally slows down.
        </Paragraph>

        <Paragraph style={{
          fontSize: 16,
          lineHeight: '28px',
          maxWidth: 1200,
          margin: '0 auto 16px'
        }}>
          A few readers covering that €{MONTHLY_COST_EUR} is the difference between this archive
          staying free and it needing ads to survive. That's the entire pitch.
        </Paragraph>
      </div>

      <Divider style={{ margin: '0 0 48px' }} />

      {/* What Your Donation Supports */}
      <Row justify="center" className="mb-48">
        <Col xs={24} lg={20}>
          <Title level={2} className="text-center mb-32" style={{ fontWeight: 700 }}>
            Where Your Support Goes
          </Title>

          <Row gutter={[24, 24]} justify="center">
            {[
              { icon: <Category set="bulk" primaryColor="#9195fc" size={32} />, title: 'Website Hosting', desc: 'Keeping the website fast and reachable online. This is a monthly bill I pay to keep things running' },
              { icon: <Discovery set="bulk" primaryColor="#9195fc" size={32} />, title: 'Home Server Upkeep', desc: 'The stories and database live on a server at my home. That means electricity, hardware wear, and occasional upgrades' },
              { icon: <Setting set="bulk" primaryColor="#9195fc" size={32} />, title: 'New Features', desc: 'Time and effort to improve the platform, fix bugs, and add new capabilities for the community' },
              { icon: <ShieldDone set="bulk" primaryColor="#9195fc" size={32} />, title: 'Backups & Safety', desc: 'Making sure every story is safe, regularly backed up, and nothing ever gets lost' }
            ].map((item) => (
              <Col xs={12} md={6} key={item.title}>
                <div className="text-center" style={{ padding: '24px 12px' }}>
                  <div style={{ marginBottom: 12 }}>{item.icon}</div>
                  <Title level={5} style={{ marginBottom: 8 }}>{item.title}</Title>
                  <Paragraph style={{ color: '#777', fontSize: 14, margin: 0 }}>{item.desc}</Paragraph>
                </div>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>

      <Divider style={{ margin: '0 0 48px' }} />

      {/* Analysis Quality Section */}
      <Row justify="center" className="mb-48">
        <Col xs={24} lg={20}>
          <Card
            bordered={false}
            style={{
              borderRadius: 20,
              background: 'linear-gradient(135deg, rgba(145, 149, 252, 0.07), rgba(191, 193, 255, 0.07))'
            }}
          >
            <Row gutter={[32, 24]} align="middle">
              <Col xs={24} md={4} className="text-center">
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(145, 149, 252, 0.15), rgba(191, 193, 255, 0.15))'
                }}>
                  <Star set="bulk" primaryColor="#9195fc" size={36} />
                </div>
              </Col>
              <Col xs={24} md={20}>
                <Title level={3} style={{ marginBottom: 8 }}>Better Analysis, More Accurate Insights</Title>
                <Paragraph style={{ color: '#555', fontSize: 15, lineHeight: '26px', marginBottom: 8 }}>
                  The NDE data currently on this site was analyzed using a language model I could afford out of
                  pocket, which, frankly, is not the most capable one out there. The results are useful,
                  but a stronger model would produce more accurate classifications.
                </Paragraph>
                <Paragraph style={{ color: '#555', fontSize: 15, lineHeight: '26px', marginBottom: 0 }}>
                  If donations grow enough to cover it, I plan to re-process the entire dataset with a
                  more capable model, upgrading the quality of every analysis on the site in one go.
                  Think of it as a one-time investment that makes the whole archive significantly more valuable.
                </Paragraph>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Divider style={{ margin: '0 0 48px' }} />

      {/* Donation Options */}
      <Row justify="center" className="mb-48">
        <Col xs={24} lg={20}>
          <Title level={2} className="text-center mb-16" style={{ fontWeight: 700 }}>
            Choose How to Donate
          </Title>

          <Paragraph className="text-center mb-32" style={{ color: '#777', fontSize: 16 }}>
            Pick the method that works best for you. Every option is secure and appreciated.
          </Paragraph>

          <Row gutter={[24, 24]} justify="center">
            {donationOptions.map((option) => (
              <Col xs={24} sm={12} lg={8} key={option.key}>
                <Card
                  hoverable
                  className="h-100 elevation text-center donate-card"
                  bordered={false}
                  style={{ borderRadius: 16, overflow: 'hidden' }}
                >
                  <div style={{ ...iconCircleStyle, background: option.bgGradient }}>
                    <span style={{ color: option.color }}>{option.icon}</span>
                  </div>

                  <Title level={3} style={{ marginBottom: 12 }}>{option.title}</Title>

                  <Paragraph style={{ color: '#555', fontSize: 15, lineHeight: '24px', minHeight: 72 }}>
                    {option.description}
                  </Paragraph>

                  {option.wallet && (
                    <Paragraph
                      copyable={{ text: option.wallet, tooltips: ['Copy address', 'Copied!'] }}
                      style={{
                        fontFamily: 'monospace',
                        fontSize: 12,
                        wordBreak: 'break-all',
                        background: 'rgba(98, 126, 234, 0.08)',
                        borderRadius: 8,
                        padding: '8px 12px',
                        marginTop: 8,
                        marginBottom: 0,
                        color: '#444'
                      }}
                    >
                      {option.wallet}
                    </Paragraph>
                  )}

                  <Button
                    type="primary"
                    size="large"
                    className="donate-option-btn"
                    style={{
                      marginTop: 16,
                      borderRadius: 8,
                      fontWeight: 600,
                      height: 44,
                      paddingLeft: 24,
                      paddingRight: 24
                    }}
                    onClick={() => {
                      if (option.wallet) {
                        navigator.clipboard.writeText(option.wallet);
                        message.success('Wallet address copied to clipboard!');
                      } else {
                        window.open(option.url, '_blank', 'noopener,noreferrer');
                      }
                    }}
                  >
                    {option.buttonText}
                  </Button>
                </Card>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>

      <Divider style={{ margin: '0 0 48px' }} />

      {/* Thank You / CTA */}
      <Row justify="center" className="mb-64">
        <Col xs={24} md={16} lg={12}>
          <Card
            bordered={false}
            style={{
              borderRadius: 20,
              background: 'linear-gradient(135deg, rgba(145, 149, 252, 0.08), rgba(191, 193, 255, 0.08))',
              textAlign: 'center',
              padding: '32px 24px'
            }}
          >
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(145, 149, 252, 0.15), rgba(191, 193, 255, 0.15))',
              marginBottom: 16
            }}>
              <Wallet set="bulk" primaryColor="#9195fc" size={28} />
            </div>

            <Title level={3} style={{ marginBottom: 12 }}>Not able to donate right now?</Title>

            <Paragraph style={{ color: '#555', fontSize: 16, lineHeight: '26px' }}>
              That's completely fine! You can still help by <strong>sharing your NDE</strong>,
              spreading the word on <strong>social media</strong>, or simply telling a
              friend about the project. Every bit of support matters.
            </Paragraph>

            <Paragraph style={{ color: '#777', fontSize: 14, marginTop: 16, marginBottom: 0 }}>
              Questions? Reach out at{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            </Paragraph>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default DonateScreen;
